import React, { useState, useRef, useEffect } from "react";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { X, UploadSimple, Star, Trash, Plus } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const blank = {
  title: "", sku: "", description: "", detailed_description: "",
  price: "", compare_at_price: "", category_ids: [], images: [], main_image: null,
  stock: "0", variants: [], condition: "Nuevo", specs: [], tags: [],
  related_ids: [], is_active: true, is_sold_out: false, is_featured: false, is_on_offer: false,
  free_shipping: false, shipping_cost: "",
  weight_kg: "", length_cm: "", width_cm: "", height_cm: "",
};

export default function ProductForm({ open, onClose, onSaved, initial, categories, allProducts }) {
  const [form, setForm] = useState(blank);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tagsText, setTagsText] = useState("");
  const fileRef = useRef();

  useEffect(() => {
    if (initial) {
      setForm({
        ...blank, ...initial,
        price: String(initial.price ?? ""),
        compare_at_price: initial.compare_at_price != null ? String(initial.compare_at_price) : "",
        stock: String(initial.stock ?? 0),
        shipping_cost: initial.shipping_cost != null ? String(initial.shipping_cost) : "",
        weight_kg: initial.weight_kg != null ? String(initial.weight_kg) : "",
        length_cm: initial.length_cm != null ? String(initial.length_cm) : "",
        width_cm: initial.width_cm != null ? String(initial.width_cm) : "",
        height_cm: initial.height_cm != null ? String(initial.height_cm) : "",
        variants: (initial.variants || []).map((v) => ({ ...v, price: v.price != null ? String(v.price) : "", stock: String(v.stock ?? 0) })),
      });
      setTagsText((initial.tags || []).join(", "));
    } else {
      setForm(blank); setTagsText("");
    }
  }, [initial, open]);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const hasVariants = form.variants.length > 0;

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const paths = [];
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        paths.push(res.data.path);
      }
      setForm((f) => ({ ...f, images: [...f.images, ...paths], main_image: f.main_image || paths[0] }));
      toast.success("Imagen(es) subida(s)");
    } catch (err) { toast.error("Error al subir imagen"); } finally { setUploading(false); }
  };

  const removeImage = (p) => setForm((f) => {
    const images = f.images.filter((x) => x !== p);
    return { ...f, images, main_image: f.main_image === p ? (images[0] || null) : f.main_image };
  });

  const toggleCat = (cid) => setForm((f) => ({
    ...f, category_ids: f.category_ids.includes(cid) ? f.category_ids.filter((x) => x !== cid) : [...f.category_ids, cid],
  }));
  const toggleRelated = (pid) => setForm((f) => ({
    ...f, related_ids: f.related_ids.includes(pid) ? f.related_ids.filter((x) => x !== pid) : [...f.related_ids, pid],
  }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.title || !form.price) { toast.error("Completa nombre y precio"); return; }
    if (form.category_ids.length === 0) { toast.error("Selecciona al menos una categoría"); return; }
    const dims = ["weight_kg", "length_cm", "width_cm", "height_cm"];
    if (dims.some((k) => form[k] === "" || form[k] == null || parseFloat(form[k]) <= 0)) {
      toast.error("Ingresa peso y dimensiones válidas (para generar guías de Envia)"); return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title, sku: form.sku || null,
        description: form.description, detailed_description: form.detailed_description,
        price: parseFloat(form.price),
        compare_at_price: form.compare_at_price ? parseFloat(form.compare_at_price) : null,
        category_ids: form.category_ids, images: form.images, main_image: form.main_image,
        stock: hasVariants ? 0 : parseInt(form.stock || "0", 10),
        variants: form.variants.map((v) => ({
          id: v.id, name: v.name, sku: v.sku || null,
          price: v.price ? parseFloat(v.price) : null, stock: parseInt(v.stock || "0", 10),
          image: v.image || null,
        })),
        condition: form.condition,
        specs: form.specs.filter((s) => s.label && s.value),
        tags: tagsText.split(",").map((t) => t.trim()).filter(Boolean),
        related_ids: form.related_ids,
        is_active: form.is_active, is_sold_out: form.is_sold_out,
        is_featured: form.is_featured, is_on_offer: form.is_on_offer,
        free_shipping: form.free_shipping,
        shipping_cost: form.free_shipping ? null : (form.shipping_cost === "" || form.shipping_cost == null ? null : parseFloat(form.shipping_cost)),
        weight_kg: parseFloat(form.weight_kg),
        length_cm: parseFloat(form.length_cm),
        width_cm: parseFloat(form.width_cm),
        height_cm: parseFloat(form.height_cm),
      };
      if (initial) await api.put(`/admin/products/${initial.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(initial ? "Producto actualizado" : "Producto creado");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo guardar"); } finally { setSaving(false); }
  };

  const inputCls = "w-full border border-black/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30";

  return (
    <div className="fixed inset-0 z-[60] bg-foreground/40 flex items-start justify-center p-3 overflow-y-auto">
      <div className="bg-white rounded-lg nb-shadow-lg w-full max-w-2xl my-6" data-testid="product-form-modal">
        <div className="flex items-center justify-between border-b border-black/10 p-4 sticky top-0 bg-white z-10 rounded-t-lg">
          <h2 className="text-lg font-extrabold tracking-tight">{initial ? "Editar producto" : "Nuevo producto"}</h2>
          <button onClick={onClose} data-testid="close-form-button" className="nb-btn bg-secondary p-1.5"><X weight="bold" size={18} /></button>
        </div>

        <form onSubmit={save} className="p-4 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="text-xs font-bold uppercase tracking-wide">Nombre</label>
              <input required value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="form-title" className={inputCls} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide">SKU</label>
              <input value={form.sku || ""} onChange={(e) => set("sku", e.target.value)} data-testid="form-sku" className={inputCls} />
            </div>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wide">Descripción corta</label>
            <input value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="form-description" className={inputCls} />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide">Descripción detallada</label>
            <textarea rows={4} value={form.detailed_description} onChange={(e) => set("detailed_description", e.target.value)} data-testid="form-detailed" className={inputCls} />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wide">Precio</label>
              <input required type="number" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="form-price" className={inputCls} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide">Precio anterior</label>
              <input type="number" step="0.01" value={form.compare_at_price} onChange={(e) => set("compare_at_price", e.target.value)} data-testid="form-compare-price" className={inputCls} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide">Condición</label>
              <Select value={form.condition} onValueChange={(v) => set("condition", v)}>
                <SelectTrigger data-testid="form-condition" className="mt-0.5 rounded-md text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-md">
                  <SelectItem value="Nuevo">Nuevo</SelectItem>
                  <SelectItem value="Usado">Usado</SelectItem>
                  <SelectItem value="Reacondicionado">Reacondicionado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide">Stock {hasVariants && "(por variante)"}</label>
              <input type="number" disabled={hasVariants} value={form.stock} onChange={(e) => set("stock", e.target.value)} data-testid="form-stock" className={`${inputCls} disabled:opacity-40`} />
            </div>
          </div>

          {/* Envío */}
          <div className="border border-black/10 rounded-lg p-3">
            <label className="text-xs font-bold uppercase tracking-wide">Envío</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1.5 items-end">
              <div>
                <Select
                  value={form.free_shipping ? "free" : (form.shipping_cost !== "" && form.shipping_cost != null ? "custom" : "store")}
                  onValueChange={(v) => {
                    if (v === "free") setForm((f) => ({ ...f, free_shipping: true, shipping_cost: "" }));
                    else if (v === "store") setForm((f) => ({ ...f, free_shipping: false, shipping_cost: "" }));
                    else setForm((f) => ({ ...f, free_shipping: false, shipping_cost: f.shipping_cost || "0" }));
                  }}>
                  <SelectTrigger data-testid="form-shipping-mode" className="rounded-md text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-md">
                    <SelectItem value="store">Usar configuración de la tienda</SelectItem>
                    <SelectItem value="free">Envío gratis</SelectItem>
                    <SelectItem value="custom">Costo de envío fijo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {!form.free_shipping && form.shipping_cost !== "" && form.shipping_cost != null && (
                <div>
                  <label className="text-[11px] text-muted-foreground">Costo de envío ($)</label>
                  <input type="number" step="0.01" min="0" value={form.shipping_cost} onChange={(e) => set("shipping_cost", e.target.value)} data-testid="form-shipping-cost" className={inputCls} />
                </div>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">
              {form.free_shipping ? "Este producto se envía gratis." : (form.shipping_cost !== "" && form.shipping_cost != null ? "Se aplicará este costo (o el más alto del carrito)." : "Usa el costo de envío general de la tienda.")}
            </p>
          </div>

          {/* Peso y dimensiones (para guías Envia) */}
          <div className="border border-black/10 rounded-lg p-3">
            <label className="text-xs font-bold uppercase tracking-wide">Peso y dimensiones del paquete <span className="text-destructive">*</span></label>
            <p className="text-[11px] text-muted-foreground mt-0.5 mb-2">Se usan para cotizar y generar la guía en Envia.com automáticamente.</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="text-[11px] text-muted-foreground">Peso (kg)</label>
                <input required type="number" step="0.01" min="0.01" value={form.weight_kg} onChange={(e) => set("weight_kg", e.target.value)} data-testid="form-weight" className={inputCls} />
              </div>
              <div>
                <label className="text-[11px] text-muted-foreground">Largo (cm)</label>
                <input required type="number" step="0.1" min="1" value={form.length_cm} onChange={(e) => set("length_cm", e.target.value)} data-testid="form-length" className={inputCls} />
              </div>
              <div>
                <label className="text-[11px] text-muted-foreground">Ancho (cm)</label>
                <input required type="number" step="0.1" min="1" value={form.width_cm} onChange={(e) => set("width_cm", e.target.value)} data-testid="form-width" className={inputCls} />
              </div>
              <div>
                <label className="text-[11px] text-muted-foreground">Alto (cm)</label>
                <input required type="number" step="0.1" min="1" value={form.height_cm} onChange={(e) => set("height_cm", e.target.value)} data-testid="form-height" className={inputCls} />
              </div>
            </div>
          </div>

          {/* Categories */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wide">Categorías</label>
            <div className="flex flex-wrap gap-2 mt-1" data-testid="form-categories">
              {categories.map((c) => (
                <button type="button" key={c.id} onClick={() => toggleCat(c.id)} data-testid={`form-cat-${c.name}`}
                        className={`px-3 py-1.5 rounded-md border text-sm ${form.category_ids.includes(c.id) ? "border-primary bg-primary/10 text-primary font-semibold" : "border-black/15 hover:border-primary"}`}>
                  {c.parent_id ? "— " : ""}{c.name}
                </button>
              ))}
            </div>
          </div>

          {/* Images */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wide">Fotos (clic en la estrella = imagen principal)</label>
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={upload} className="hidden" data-testid="form-image-input" />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                    className="w-full nb-btn bg-secondary py-2 mt-1 flex items-center justify-center gap-2 hover:bg-secondary/70 text-sm">
              <UploadSimple weight="bold" size={16} /> {uploading ? "Subiendo…" : "Subir imágenes"}
            </button>
            {form.images.length > 0 && (
              <div className="flex gap-2 mt-2 flex-wrap">
                {form.images.map((p) => (
                  <div key={p} className="relative w-20 h-20 rounded-md overflow-hidden border border-black/10 group">
                    <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => set("main_image", p)} title="Imagen principal"
                            className={`absolute top-0.5 left-0.5 p-0.5 rounded ${form.main_image === p ? "text-primary" : "text-white"}`}>
                      <Star weight={form.main_image === p ? "fill" : "regular"} size={16} />
                    </button>
                    <button type="button" onClick={() => removeImage(p)} className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded p-0.5">
                      <X weight="bold" size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Variants */}
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold uppercase tracking-wide">Variantes (talla, color, etc.)</label>
              <button type="button" onClick={() => set("variants", [...form.variants, { id: crypto.randomUUID(), name: "", sku: "", price: "", stock: "0", image: null }])}
                      data-testid="add-variant-button" className="text-xs text-primary font-semibold hover:underline">+ Agregar variante</button>
            </div>
            <div className="space-y-2 mt-1">
              {form.variants.map((v, i) => (
                <div key={v.id} className="border border-black/10 rounded-md p-2 space-y-2" data-testid={`variant-row-${i}`}>
                  <div className="grid grid-cols-12 gap-2">
                    <input placeholder="Nombre (ej: Talla M / Rojo)" value={v.name} onChange={(e) => { const cp = [...form.variants]; cp[i] = { ...cp[i], name: e.target.value }; set("variants", cp); }} className={`${inputCls} col-span-5`} />
                    <input placeholder="SKU" value={v.sku || ""} onChange={(e) => { const cp = [...form.variants]; cp[i] = { ...cp[i], sku: e.target.value }; set("variants", cp); }} className={`${inputCls} col-span-3`} />
                    <input placeholder="Precio" type="number" value={v.price} onChange={(e) => { const cp = [...form.variants]; cp[i] = { ...cp[i], price: e.target.value }; set("variants", cp); }} className={`${inputCls} col-span-2`} />
                    <input placeholder="Stock" type="number" value={v.stock} onChange={(e) => { const cp = [...form.variants]; cp[i] = { ...cp[i], stock: e.target.value }; set("variants", cp); }} className={`${inputCls} col-span-1`} />
                    <button type="button" onClick={() => set("variants", form.variants.filter((_, idx) => idx !== i))} className="col-span-1 nb-btn bg-secondary hover:bg-destructive hover:text-white flex items-center justify-center"><Trash weight="bold" size={14} /></button>
                  </div>
                  {form.images.length > 0 ? (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[11px] text-muted-foreground mr-1">Foto de la variante:</span>
                      {form.images.map((img) => (
                        <button type="button" key={img} onClick={() => { const cp = [...form.variants]; cp[i] = { ...cp[i], image: cp[i].image === img ? null : img }; set("variants", cp); }}
                                data-testid={`variant-${i}-img-${form.images.indexOf(img)}`}
                                className={`w-9 h-9 rounded overflow-hidden border-2 transition-all ${v.image === img ? "border-primary scale-105" : "border-transparent opacity-60 hover:opacity-100"}`}>
                          <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[11px] text-muted-foreground">Sube fotos arriba para poder asignar una a esta variante.</p>
                  )}
                </div>
              ))}
              {form.variants.length === 0 && <p className="text-xs text-muted-foreground">Sin variantes. Se usa el stock general.</p>}
            </div>
          </div>

          {/* Specs */}
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold uppercase tracking-wide">Características</label>
              <button type="button" onClick={() => set("specs", [...form.specs, { label: "", value: "" }])} className="text-xs text-primary font-semibold hover:underline">+ Agregar</button>
            </div>
            <div className="space-y-2 mt-1">
              {form.specs.map((s, i) => (
                <div key={i} className="flex gap-2">
                  <input placeholder="Etiqueta" value={s.label} onChange={(e) => { const cp = [...form.specs]; cp[i] = { ...cp[i], label: e.target.value }; set("specs", cp); }} className={inputCls} />
                  <input placeholder="Valor" value={s.value} onChange={(e) => { const cp = [...form.specs]; cp[i] = { ...cp[i], value: e.target.value }; set("specs", cp); }} className={inputCls} />
                  <button type="button" onClick={() => set("specs", form.specs.filter((_, idx) => idx !== i))} className="nb-btn bg-secondary px-2 hover:bg-destructive hover:text-white"><X weight="bold" size={14} /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wide">Etiquetas (separadas por coma)</label>
            <input value={tagsText} onChange={(e) => setTagsText(e.target.value)} placeholder="audio, bluetooth, oferta" data-testid="form-tags" className={inputCls} />
          </div>

          {/* Related */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wide">Productos relacionados</label>
            <div className="max-h-32 overflow-y-auto border border-black/10 rounded-md p-2 mt-1 space-y-1">
              {allProducts.filter((p) => !initial || p.id !== initial.id).map((p) => (
                <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-secondary/50 rounded px-1 py-0.5">
                  <input type="checkbox" checked={form.related_ids.includes(p.id)} onChange={() => toggleRelated(p.id)} />
                  <span className="line-clamp-1">{p.title}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Toggles */}
          <div className="grid grid-cols-2 gap-3 border-t border-black/10 pt-4">
            {[["is_active", "Activo"], ["is_featured", "Destacado"], ["is_on_offer", "En oferta"], ["is_sold_out", "Marcar agotado"]].map(([k, label]) => (
              <label key={k} className="flex items-center justify-between border border-black/10 rounded-md px-3 py-2 text-sm">
                <span>{label}</span>
                <Switch checked={form[k]} onCheckedChange={(v) => set(k, v)} data-testid={`toggle-${k}`} />
              </label>
            ))}
          </div>

          <button type="submit" disabled={saving} data-testid="submit-product-button"
                  className="w-full nb-btn bg-primary text-primary-foreground py-3 hover:brightness-110 disabled:opacity-50">
            {saving ? "Guardando…" : initial ? "Guardar cambios" : "Crear producto"}
          </button>
        </form>
      </div>
    </div>
  );
}
